import { CustomSqlError } from './classify'
import { identifier, keyword, tableDefinition, tokenize } from './lexer'

export interface EnumOption {
  label: string
  /** Engine ids may exceed JavaScript's safe integer range. */
  id: string
  pos: string
  color: string
}

/** Catalog names may retain SQL quoting; resolved definitions carry identity. */
export function enumTypeName(sql: string): string {
  const header = tokenize(sql)
  if (!keyword(header[0], 'CREATE') || !keyword(header[1], 'TYPE') || !identifier(header[2]) || !keyword(header[3], 'AS') || !keyword(header[4], 'ENUM')) throw new CustomSqlError('E_SQL', 'Expected a resolved CREATE TYPE AS ENUM')
  return header[2].value
}

/** Native array result text uses PostgreSQL braces, quoting and backslash escapes. */
export function enumArrayLabels(value: string): string[] {
  if (!value.startsWith('{') || !value.endsWith('}')) throw new CustomSqlError('E_SQL', 'Expected a native enum array')
  const body = value.slice(1, -1)
  if (body === '') return []
  const labels: string[] = []
  let escaped = false, label = '', quoted = false
  for (const character of body) {
    if (escaped) { label += character; escaped = false }
    else if (character === '\\') escaped = true
    else if (character === '"') quoted = !quoted
    else if (character === ',' && !quoted) { labels.push(label); label = '' }
    else label += character
  }
  if (quoted || escaped) throw new CustomSqlError('E_SQL', 'Malformed native enum array')
  labels.push(label)
  return labels
}

/** Read resolved catalog text; option identity and order belong to the engine. */
export function enumOptions(sql: string): EnumOption[] {
  const header = tokenize(sql)
  if (!keyword(header[0], 'CREATE') || !keyword(header[1], 'TYPE') ||
      !identifier(header[2]) || !keyword(header[3], 'AS') || !keyword(header[4], 'ENUM')) {
    throw new CustomSqlError('E_SQL', 'Expected a resolved CREATE TYPE AS ENUM')
  }
  const options = tableDefinition(sql).parts.filter(part => part.length > 0).map(part => {
    const tokens = tokenize(part)
    const fail = (): never => { throw new CustomSqlError('E_SQL', 'Incomplete resolved enum identity') }
    const label = tokens[0]
    if (label?.type !== 'string' || !keyword(tokens[1], 'WITH') || tokens[2]?.raw !== '(') return fail()
    const attributes = new Map<string, string>()
    let at = 3
    while (tokens[at]?.raw !== ')') {
      const name = tokens[at]
      if (name?.type !== 'word' || tokens[at + 1]?.raw !== '=') return fail()
      const key = name.value.toLowerCase()
      if (!['id', 'pos', 'color'].includes(key) || attributes.has(key)) return fail()
      at += 2
      const negative = key === 'id' && tokens[at]?.raw === '-'
      if (negative) at++
      const value = tokens[at]
      if (key === 'id' ? value?.type !== 'number' || !/^\d+$/u.test(value.value) : value?.type !== 'string') return fail()
      attributes.set(key, `${negative ? '-' : ''}${value.value}`)
      at++
      if (tokens[at]?.raw === ')') break
      if (tokens[at]?.raw !== ',' || tokens[at + 1]?.raw === ')') return fail()
      at++
    }
    const id = attributes.get('id')
    const pos = attributes.get('pos')
    const color = attributes.get('color')
    if (id === undefined || pos === undefined || color === undefined || at !== tokens.length - 1) return fail()
    return { label: label.value, id, pos, color }
  })
  return options.toSorted((left, right) => left.pos < right.pos ? -1 : left.pos > right.pos ? 1 : 0)
}
